import { useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../Store/actions";
import { strings } from "../i18n";
import { logToSentry } from "../../config/Sentry";
import usePendingCountProgress from "./use-pending-count-progress";

const storageKey = (auditId, action) => `cvPendingCountAck:${action}:${auditId}`;

/** Acknowledgement is per audit, per action, and lasts the browser session. */
const wasAcknowledged = (auditId, action) => {
    try {
        return sessionStorage.getItem(storageKey(auditId, action)) === "1";
    } catch (error) {
        // Private windows and blocked site data throw here. Asking again is the
        // safe failure — never treat an unreadable store as "already agreed".
        return false;
    }
};

const rememberAcknowledged = (auditId, action) => {
    try {
        sessionStorage.setItem(storageKey(auditId, action), "1");
    } catch (error) {
        // Not fatal: the controller is asked again on the next action.
    }
};

const recordMessageKey = (action) =>
    action === "reject"
        ? "messages.pendingCountRejectionRecord"
        : "messages.pendingCountApprovalRecord";

const acknowledgementErrorKey = (action) =>
    action === "reject"
        ? "errors.onPendingCountRejectionAcknowledgement"
        : "errors.onPendingCountAcknowledgement";

/**
 * Use Pending Count Approval Hook
 *
 * Gates approvals and rejections made while an audit is still in Pending Count.
 * The controller is shown the disclaimer once per audit *per action* — approving
 * an asset does not silence the warning on reject, or the other way round,
 * because a reject reissues the asset before the auditor's device has synced,
 * which is a different consequence to acting on stale/incomplete data. Each
 * action has to be acknowledged, and the acknowledgement is written to the
 * audit's comments so there is a record of who acted against an unfinished
 * count and how far it had got.
 *
 * Wrap a handler in `guardApproval` or `guardReject`. If no acknowledgement is
 * needed the handler runs immediately, so every other audit status is untouched.
 */
export default () => {
    const counts = usePendingCountProgress();
    const audits = useSelector((state) => state.audits);
    const auth = useSelector((state) => state.auth);
    const dispatch = useDispatch();

    const [action, setAction] = useState(null); // null | "approve" | "reject"
    const [recording, setRecording] = useState(false);
    const pendingAction = useRef(null);

    const auditId = audits?.item?.id;

    const guard = useCallback(
        (nextAction, proceed) => {
            if (!counts || wasAcknowledged(auditId, nextAction)) return proceed();
            pendingAction.current = proceed;
            setAction(nextAction);
            return undefined;
        },
        [counts, auditId]
    );

    const guardApproval = useCallback(
        (proceed) => guard("approve", proceed),
        [guard]
    );
    const guardReject = useCallback(
        (proceed) => guard("reject", proceed),
        [guard]
    );

    const onCancel = useCallback(() => {
        pendingAction.current = null;
        setAction(null);
    }, []);

    const onConfirm = useCallback(async () => {
        setRecording(true);
        const timestamp = new Date().toISOString();
        const userId = auth?.account?.id;

        try {
            await dispatch(
                actions.auditComments.create.postRequest({
                    audit: Number(auditId),
                    content: strings(recordMessageKey(action), {
                        counted: counts.counted,
                        total: counts.total,
                        user: auth?.account?.displayName || userId
                    }),
                    created_by: userId,
                    created_date: timestamp,
                    updated_by: userId,
                    updated_date: timestamp
                })
            );
        } catch (error) {
            // The action still goes ahead — refusing it because an audit-trail
            // write failed would block the controller's actual work — but the
            // failure is surfaced rather than swallowed, because the record is
            // the point of the disclaimer.
            logToSentry("On recording pending count acknowledgement:", {
                error
            });
            dispatch(
                actions.toast.create.error(
                    strings(acknowledgementErrorKey(action))
                )
            );
        }

        rememberAcknowledged(auditId, action);
        setRecording(false);
        setAction(null);

        const proceed = pendingAction.current;
        pendingAction.current = null;
        if (proceed) await proceed();
    }, [action, auditId, auth, counts, dispatch]);

    return {
        counts,
        guardApproval,
        guardReject,
        dialogProps: {
            open: action !== null,
            action,
            counts,
            loading: recording,
            onCancel,
            onConfirm
        }
    };
};
